import React, { useState } from 'react';
import { X, Sparkles, Send, Loader2, Copy, Check, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROBOT_IMAGE = "/mascot-hero.png";

const MESSAGE_TYPES = [
  { value: 'sms', label: 'SMS (160 chars)', limit: 160 },
  { value: 'push', label: 'Push Notification (100 chars)', limit: 100 },
  { value: 'email_subject', label: 'Email Subject (60 chars)', limit: 60 },
  { value: 'email_body', label: 'Email Body (500 chars)', limit: 500 },
  { value: 'social', label: 'Social Media (280 chars)', limit: 280 },
];

const TONES = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'excited', label: 'Excited' },
  { value: 'professional', label: 'Professional' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'playful', label: 'Playful' },
  { value: 'luxurious', label: 'Luxurious' },
];

const TABS = [
  { id: 'generate', label: 'Generate', icon: <Sparkles size={16} /> },
  { id: 'templates', label: 'Templates', icon: <Copy size={16} /> },
];

const TEMPLATES = [
  { title: 'Happy Hour', desc: 'Half-price appetizers and $5 cocktails', occasion: 'Happy Hour', tone: 'excited' },
  { title: 'Weekend Special', desc: 'Buy one entree, get one free this Saturday', occasion: 'Weekend', tone: 'friendly' },
  { title: 'Birthday Promo', desc: 'Free dessert on your birthday when you dine with us', occasion: 'Birthday', tone: 'playful' },
  { title: 'Lunch Deal', desc: 'Lunch combo starting at $9.99 — soup, salad, and sandwich', occasion: 'Lunch', tone: 'professional' },
  { title: 'Holiday Special', desc: '20% off all family platters for the holiday weekend', occasion: 'Holiday', tone: 'excited' },
  { title: 'New Item Launch', desc: 'Try our new loaded truffle fries — available now', occasion: 'Launch', tone: 'excited' },
];

export const PromoPal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [activeTab, setActiveTab] = useState('generate');
  const [promotion, setPromotion] = useState('');
  const [messageType, setMessageType] = useState('sms');
  const [restaurantName, setRestaurantName] = useState('');
  const [occasion, setOccasion] = useState('');
  const [tone, setTone] = useState('friendly');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!promotion.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(`${API_URL}/api/promo/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promotion: promotion.trim(),
          message_type: messageType,
          restaurant_name: restaurantName.trim() || null,
          occasion: occasion.trim() || null,
          tone,
        }),
      });
      if (!res.ok) throw new Error('Failed to generate');
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (result) {
      navigator.clipboard.writeText(result.message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTemplate = (template) => {
    setPromotion(template.desc);
    setOccasion(template.occasion);
    setTone(template.tone);
    setActiveTab('generate');
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const selectedType = MESSAGE_TYPES.find(t => t.value === messageType);

  return (
    <>
      {/* Floating Robot Button */}
      {!isOpen && (
        <>
          {showHint && (
            <div
              data-testid="promo-pal-hint"
              className="promo-pal-hint-bubble"
              style={{
                position: 'fixed',
                bottom: '180px',
                left: '170px',
                zIndex: 9998,
                pointerEvents: 'none',
              }}
            >
              <div style={{
                background: '#FFD700',
                color: '#000',
                padding: '10px 20px',
                borderRadius: '4px',
                fontWeight: 800,
                fontSize: '1.1rem',
                whiteSpace: 'nowrap',
                border: '3px solid #000',
                boxShadow: '3px 3px 0px 0px #000',
                animation: 'hintPulse 1.5s ease-in-out infinite',
                textTransform: 'uppercase',
              }}>
                Click me!
              </div>
              {/* Curved arrow pointing down-left to the mascot */}
              <svg width="50" height="44" viewBox="0 0 40 36" style={{ marginLeft: '4px', marginTop: '-2px' }}>
                <path d="M30 2 C 20 4, 10 14, 6 30" stroke="#000" strokeWidth="3" fill="none" strokeLinecap="round" />
                <polygon points="2,28 6,36 10,27" fill="#000" />
              </svg>
            </div>
          )}
          <button
            data-testid="promo-pal-btn"
            onClick={() => { setIsOpen(true); setShowHint(false); }}
          className="promo-pal-mascot"
          style={{
            position: 'fixed',
            bottom: '10px',
            left: '10px',
            width: '160px',
            height: '220px',
            borderRadius: '0',
            border: 'none',
            cursor: 'pointer',
            zIndex: 9998,
            padding: 0,
            background: 'transparent',
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            overflow: 'visible',
          }}
        >
          <img src={ROBOT_IMAGE} alt="Promo Pal" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </button>
        </>
      )}

      {/* Modal Overlay */}
      {isOpen && (
        <div
          data-testid="promo-pal-modal"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <div style={{
            width: '95%',
            maxWidth: '680px',
            maxHeight: '90vh',
            borderRadius: '20px',
            overflow: 'hidden',
            background: '#fafafa',
            boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Header with gradient */}
            <div style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 40%, #d946ef 70%, #ec4899 100%)',
              padding: '2rem 2rem 1.5rem',
              position: 'relative',
            }}>
              <button
                data-testid="promo-pal-close"
                onClick={handleClose}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white',
                }}
              >
                <X size={18} />
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Sparkles size={24} color="white" />
                </div>
                <div>
                  <h2 style={{ color: 'white', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.2 }}>
                    AI Message Generator
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem' }}>
                    Create professional marketing messages in seconds
                  </p>
                </div>
              </div>

              {/* Feature pills */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                {[
                  { icon: <Sparkles size={14} />, label: 'Generate Messages', desc: 'AI-powered copy' },
                  { icon: <Send size={14} />, label: 'Multi-Channel', desc: 'SMS, Push, Email' },
                  { icon: <Copy size={14} />, label: 'Templates', desc: 'Quick start' },
                ].map((f, i) => (
                  <div key={i} style={{
                    background: 'rgba(255,255,255,0.15)',
                    borderRadius: '10px',
                    padding: '0.625rem 0.875rem',
                    backdropFilter: 'blur(8px)',
                    flex: 1,
                    minWidth: '140px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'white', fontWeight: 600, fontSize: '0.8125rem', marginBottom: '0.125rem' }}>
                      {f.icon} {f.label}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>{f.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div style={{
              display: 'flex',
              borderBottom: '1px solid #e5e7eb',
              background: 'white',
            }}>
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  data-testid={`promo-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    border: 'none',
                    background: activeTab === tab.id ? 'white' : 'transparent',
                    borderBottom: activeTab === tab.id ? '2px solid #7c3aed' : '2px solid transparent',
                    color: activeTab === tab.id ? '#7c3aed' : '#6b7280',
                    fontWeight: activeTab === tab.id ? 600 : 400,
                    fontSize: '0.9375rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
              {activeTab === 'generate' && (
                <div>
                  {!result ? (
                    <>
                      <h3 style={{ fontWeight: 600, fontSize: '1.0625rem', color: '#000', marginBottom: '0.25rem' }}>
                        Generate New Message
                      </h3>
                      <p style={{ color: '#000', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
                        Describe what you want to promote and let AI create the perfect message
                      </p>

                      {/* Promotion textarea */}
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9375rem', color: '#000', marginBottom: '0.375rem' }}>
                          What do you want to promote? <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <textarea
                          data-testid="promo-promotion-input"
                          value={promotion}
                          onChange={(e) => setPromotion(e.target.value)}
                          placeholder="E.g., Lunch special with 20% off all pasta dishes this week"
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '10px',
                            fontSize: '0.9375rem',
                            color: '#000',
                            resize: 'vertical',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                      </div>

                      {/* Message Type */}
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9375rem', color: '#000', marginBottom: '0.375rem' }}>
                          Message Type
                        </label>
                        <div style={{ position: 'relative' }}>
                          <select
                            data-testid="promo-message-type"
                            value={messageType}
                            onChange={(e) => setMessageType(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              paddingRight: '2.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '10px',
                              fontSize: '0.9375rem',
                              color: '#000',
                              appearance: 'none',
                              background: 'white',
                              outline: 'none',
                              cursor: 'pointer',
                            }}
                          >
                            {MESSAGE_TYPES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                          <ChevronDown size={18} color="#6b7280" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                        </div>
                      </div>

                      {/* Two column: Restaurant Name + Occasion */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9375rem', color: '#000', marginBottom: '0.375rem' }}>
                            Restaurant Name <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                          </label>
                          <input
                            data-testid="promo-restaurant-name"
                            type="text"
                            value={restaurantName}
                            onChange={(e) => setRestaurantName(e.target.value)}
                            placeholder="Your Restaurant Name"
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '10px',
                              fontSize: '0.9375rem',
                              color: '#000',
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9375rem', color: '#000', marginBottom: '0.375rem' }}>
                            Occasion <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                          </label>
                          <input
                            data-testid="promo-occasion"
                            type="text"
                            value={occasion}
                            onChange={(e) => setOccasion(e.target.value)}
                            placeholder="E.g., Weekend, Happy Hour"
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '10px',
                              fontSize: '0.9375rem',
                              color: '#000',
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                          />
                        </div>
                      </div>

                      {/* Tone */}
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9375rem', color: '#000', marginBottom: '0.375rem' }}>
                          Tone
                        </label>
                        <div style={{ position: 'relative' }}>
                          <select
                            data-testid="promo-tone"
                            value={tone}
                            onChange={(e) => setTone(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              paddingRight: '2.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '10px',
                              fontSize: '0.9375rem',
                              color: '#000',
                              appearance: 'none',
                              background: 'white',
                              outline: 'none',
                              cursor: 'pointer',
                            }}
                          >
                            {TONES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                          <ChevronDown size={18} color="#6b7280" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                        </div>
                      </div>

                      {error && (
                        <div data-testid="promo-error" style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '1rem', textAlign: 'center' }}>
                          {error}
                        </div>
                      )}

                      {/* Generate Button */}
                      <button
                        data-testid="promo-generate-btn"
                        onClick={handleGenerate}
                        disabled={loading || !promotion.trim()}
                        style={{
                          width: '100%',
                          padding: '0.875rem',
                          background: loading || !promotion.trim() ? '#d1d5db' : 'linear-gradient(135deg, #7c3aed 0%, #c026d3 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '1rem',
                          fontWeight: 600,
                          cursor: loading || !promotion.trim() ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          transition: 'opacity 0.2s',
                        }}
                      >
                        {loading ? (
                          <>
                            <Loader2 size={20} style={{ animation: 'promoPalSpin 1s linear infinite' }} />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles size={20} />
                            Generate Message
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    /* Result View */
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '1rem',
                      }}>
                        <h3 style={{ fontWeight: 600, fontSize: '1.0625rem', color: '#000' }}>
                          Generated Message
                        </h3>
                        <span style={{
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.625rem',
                          borderRadius: '9999px',
                          background: result.char_count <= result.char_limit ? '#dcfce7' : '#fef2f2',
                          color: result.char_count <= result.char_limit ? '#16a34a' : '#dc2626',
                          fontWeight: 600,
                        }}>
                          {result.char_count}/{result.char_limit} chars
                        </span>
                      </div>

                      <div data-testid="promo-result" style={{
                        background: 'white',
                        border: '2px solid #7c3aed',
                        borderRadius: '12px',
                        padding: '1.25rem',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        color: '#111',
                        marginBottom: '1rem',
                        position: 'relative',
                      }}>
                        {result.message}
                      </div>

                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                          data-testid="promo-copy-btn"
                          onClick={handleCopy}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            background: copied ? '#16a34a' : '#7c3aed',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            transition: 'background 0.2s',
                          }}
                        >
                          {copied ? <><Check size={18} /> Copied!</> : <><Copy size={18} /> Copy Message</>}
                        </button>
                        <button
                          data-testid="promo-regenerate-btn"
                          onClick={() => { setResult(null); }}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            background: 'white',
                            color: '#7c3aed',
                            border: '2px solid #7c3aed',
                            borderRadius: '10px',
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          <Sparkles size={18} /> Try Again
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'templates' && (
                <div>
                  <h3 style={{ fontWeight: 600, fontSize: '1.0625rem', color: '#000', marginBottom: '0.25rem' }}>
                    Quick Templates
                  </h3>
                  <p style={{ color: '#000', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
                    Start with a template and customize it
                  </p>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {TEMPLATES.map((t, i) => (
                      <button
                        key={i}
                        data-testid={`promo-template-${i}`}
                        onClick={() => handleTemplate(t)}
                        style={{
                          textAlign: 'left',
                          padding: '1rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          background: 'white',
                          cursor: 'pointer',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(124, 58, 237, 0.15)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        <div style={{ fontWeight: 600, color: '#000', marginBottom: '0.25rem', fontSize: '0.9375rem' }}>
                          {t.title}
                        </div>
                        <div style={{ color: '#000', fontSize: '0.8125rem' }}>
                          {t.desc}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <span style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', background: '#f3e8ff', color: '#7c3aed', fontWeight: 500 }}>
                            {t.occasion}
                          </span>
                          <span style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', background: '#ede9fe', color: '#6d28d9', fontWeight: 500 }}>
                            {t.tone}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default PromoPal;
